import { createSelector } from '@reduxjs/toolkit';

import { RootState } from '../../types';

const signingIds = (state: RootState) =>
  state.realizationPositions.data.signingIds;
const ordersItems = (state: RootState) => state.orderDetailed.data.order?.items;
const positions = (state: RootState) =>
  state.realizationPositions.data.info.positions;
const sorts = (state: RootState) => state.realizationPositions.data.sorts;

const adjustedPositions = (state: RootState) =>
  state.realizationPositions.data.adjustmentPositions;
const adjustedSorts = (state: RootState) =>
  state.realizationPositions.data.adjustmentSorts;

const signingOrders = createSelector([ordersItems, signingIds], (items, ids) =>
  items?.filter((item) => ids.indexOf(item.id) !== -1)
);

const changedAdjustmentPositions = createSelector(adjustedPositions, (pos) =>
  pos?.filter((item) => item.sum !== null || item.count !== null)
);

const sortedAdjustedPositions = createSelector(
  [adjustedPositions, adjustedSorts],
  (adjPositions, adjSorts) => {
    const { oldSum, sum, count, oldCount } = adjSorts;
    let sortedPos = adjPositions;

    if (sortedPos) {
      if (oldSum.active) {
        sortedPos = sortedPos.slice().sort((a, b) => {
          if (oldSum.direction === 'ASC') {
            if (a.oldSum < b.oldSum) {
              return 1;
            }

            if (a.oldSum === b.oldSum) {
              return 0;
            }

            return -1;
          }
          if (a.oldSum > b.oldSum) {
            return 1;
          }

          if (a.oldSum === b.oldSum) {
            return 0;
          }

          return -1;
        });
      }

      if (sum.active) {
        sortedPos = sortedPos.slice().sort((a, b) => {
          if (sum.direction === 'ASC') {
            if (a.sum !== null && b.sum !== null && a.sum < b.sum) {
              return 1;
            }

            if (a.sum === null && b.sum && b.sum > 0) {
              return 1;
            }

            if (b.sum === null && a.sum && a.sum > 0) {
              return -1;
            }

            if (a.sum === b.sum) {
              return 0;
            }

            return -1;
          }

          if (a.sum !== null && b.sum !== null && a.sum > b.sum) {
            return 1;
          }

          if (a.sum === null && b.sum && b.sum > 0) {
            return -1;
          }

          if (b.sum === null && a.sum && a.sum > 0) {
            return 1;
          }

          if (a.sum === b.sum) {
            return 0;
          }

          return -1;
        });
      }

      if (oldCount.active) {
        sortedPos = sortedPos.slice().sort((a, b) => {
          if (oldCount.direction === 'ASC') {
            if (a.oldCount < b.oldCount) {
              return 1;
            }

            if (a.oldCount === b.oldCount) {
              return 0;
            }

            return -1;
          }
          if (a.oldCount > b.oldCount) {
            return 1;
          }

          if (a.oldCount === b.oldCount) {
            return 0;
          }

          return -1;
        });
      }

      if (count.active) {
        sortedPos = sortedPos.slice().sort((a, b) => {
          if (count.direction === 'ASC') {
            if (a.count !== null && b.count !== null && a.count < b.count) {
              return 1;
            }

            if (a.count === null && b.count && b.count > 0) {
              return 1;
            }

            if (b.count === null && a.count && a.count > 0) {
              return -1;
            }

            if (a.count === b.count) {
              return 0;
            }

            return -1;
          }
          if (a.count !== null && b.count !== null && a.count > b.count) {
            return 1;
          }

          if (a.sum === null && b.sum && b.sum > 0) {
            return -1;
          }

          if (b.sum === null && a.sum && a.sum > 0) {
            return 1;
          }

          if (a.count === b.count) {
            return 0;
          }

          return -1;
        });
      }
    }

    return sortedPos ?? [];
  }
);

const sortedSigningOrders = createSelector(
  [signingOrders, sorts],
  (orders, sortsData) => {
    const { count, sum, positions: sortedPos } = sortsData;

    let sorted = orders;

    if (sorted) {
      if (sortedPos.active) {
        sorted = sorted.slice().sort((a, b) => {
          if (sortedPos.direction === 'ASC') {
            if (a.positionCount < b.positionCount) {
              return 1;
            }
            if (a.positionCount === b.positionCount) {
              return 0;
            }

            return -1;
          }
          if (a.positionCount < b.positionCount) {
            return -1;
          }
          if (a.positionCount === b.positionCount) {
            return 0;
          }

          return 1;
        });
      }

      if (count.active) {
        sorted = sorted.slice().sort((a, b) => {
          if (count.direction === 'ASC') {
            if (a.totalCountItem < b.totalCountItem) {
              return 1;
            }
            if (a.totalCountItem === b.totalCountItem) {
              return 0;
            }

            return -1;
          }
          if (a.totalCountItem < b.totalCountItem) {
            return -1;
          }
          if (a.totalCountItem === b.totalCountItem) {
            return 0;
          }

          return 1;
        });
      }

      if (sum.active) {
        sorted = sorted.slice().sort((a, b) => {
          if (sum.direction === 'ASC') {
            if (a.price < b.price) {
              return 1;
            }
            if (a.price === b.price) {
              return 0;
            }

            return -1;
          }
          if (a.price < b.price) {
            return -1;
          }
          if (a.price === b.price) {
            return 0;
          }

          return 1;
        });
      }
    }

    return sorted;
  }
);

const sortedPositions = createSelector([positions, sorts], (pos, sortsData) => {
  const { count, sum } = sortsData;

  let sorted = pos;
  if (sorted) {
    if (count.active) {
      sorted = sorted.slice().sort((a, b) => {
        if (count.direction === 'ASC') {
          if (a.count < b.count) {
            return 1;
          }
          if (a.count === b.count) {
            return 0;
          }

          return -1;
        }
        if (a.count < b.count) {
          return -1;
        }
        if (a.count === b.count) {
          return 0;
        }

        return 1;
      });
    }

    if (sum.active) {
      sorted = sorted.slice().sort((a, b) => {
        if (sum.direction === 'ASC') {
          if (a.price < b.price) {
            return 1;
          }
          if (a.price === b.price) {
            return 0;
          }

          return -1;
        }
        if (a.price < b.price) {
          return -1;
        }
        if (a.price === b.price) {
          return 0;
        }

        return 1;
      });
    }
  }

  return sorted ?? [];
});

export {
  signingOrders,
  sortedPositions,
  sortedSigningOrders,
  sortedAdjustedPositions,
  changedAdjustmentPositions,
};
